import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { jobsEndpoint } from "../constants";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import GoogleMeetIcon from "../assets/icons/googleMeet.png";

// Helper function to calculate event position and height
const calculateEventPosition = (interviewDate) => {
  const start = moment(interviewDate);
  const startHour = start.hours();
  const startMinute = start.minutes();
  const pixelsPerHour = 96;
  const startOfDayHour = 8;
  const topOffset = (startHour - startOfDayHour + startMinute / 60) * pixelsPerHour;
  const eventHeight = pixelsPerHour;
  return { top: topOffset, height: eventHeight };
};

// Helper function to get event color based on status
const getEventColor = (status) => {
  switch (status) {
    case "upcoming":
      return "#EFF8FF";
    case "completed":
      return "#EDFCF2";
    case "cancelled":
      return "#FEF6EE";
    default:
      return "#FAFAFA";
  }
};

const getEventBorderColor = (status) => {
  switch (status) {
    case "upcoming":
      return "#B2DDFF";
    case "completed":
      return "#AAF0C4";
    case "cancelled":
      return "#F9DBAF";
    default:
      return "#E9EAEB";
  }
};

const Calendar = () => {
  const [page, setPage] = useState("weekView");
  const [interviews, setInterviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [newMeeting, setNewMeeting] = useState({
    job: "",
    interviewee: "",
    interview_date: "",
    interview_time: "",
    notes: "",
    duration_minutes: 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [loading, setLoading] = useState(false);
  const [googleMeetDetails, setGoogleMeetDetails] = useState(null);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setGoogleMeetDetails(null);
    setNewMeeting({
      job: "",
      interviewee: "",
      interview_date: "",
      interview_time: "",
      notes: "",
      duration_minutes: 60,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const token = localStorage.getItem('token');

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "16px",
  };

  useEffect(() => {
    fetchInterviews();
    fetchJobs();
  }, []);

  // Load applicants when job is selected
  useEffect(() => {
    if (newMeeting.job) {
      fetchApplicants(newMeeting.job);
    } else {
      setApplicants([]);
    }
  }, [newMeeting.job]);

  const fetchInterviews = async () => {
    try {
      const response = await axios.get(jobsEndpoint + "my-interviews/", {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        }
      });
      console.log(response.data.results);
      setInterviews(response.data.results);
    } catch (error) {
      console.error("Error fetching interviews:", error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(jobsEndpoint + "list", {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      setJobs(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchApplicants = async (jobId) => {
    setLoading(true);
    try {
      const response = await axios.get(jobsEndpoint + `${jobId}/applicant_list`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        }
      });
      setApplicants(response.data.applicants || []);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setNewMeeting({
      ...newMeeting,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!newMeeting.job) {
        alert("Please select a job");
        return;
      }
      if (!newMeeting.interviewee) {
        alert("Please select an interviewee");
        return;
      }
      if (!newMeeting.interview_date || !newMeeting.interview_time) {
        alert("Please select date and time");
        return;
      }

      // Combine date and time into ISO format
      const dateTimeString = `${newMeeting.interview_date}T${newMeeting.interview_time}`;
      const localDate = new Date(dateTimeString);
      const isoDate = localDate.toISOString();

      // Prepare payload for the new endpoint
      const payload = {
        interviewee: newMeeting.interviewee, // Applicant ID
        interview_date: isoDate,
        duration_minutes: parseInt(newMeeting.duration_minutes, 10),
        notes: newMeeting.notes,
        timezone: newMeeting.timezone,
        is_upcoming: true
      };

      console.log('Sending payload:', payload);

      // Call the new endpoint: /<job_id>/interview_create/
      const response = await axios.post(
        `${jobsEndpoint}${newMeeting.job}/interview_create/`,
        payload,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      console.log("Interview scheduled:", response.data);

      // Store Google Meet details if available
      if (response.data.google_meet_link) {
        setGoogleMeetDetails({
          meet_link: response.data.google_meet_link,
          meeting_code: response.data.meeting_code
        });
      } else {
        handleClose();
      }

      // Refresh interviews
      fetchInterviews();
      
      // Show success message
      alert('Interview scheduled successfully!');
    } catch (error) {
      console.error("Error scheduling interview:", error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
  };


  return (
    <div>
      <div className="flex justify-start items-start flex-row">
        <div className="flex flex-1 justify-start items-start flex-col pt-2">
          <div className="flex self-stretch justify-start items-start flex-col gap-8  pb-[48px] ">
            <div className="flex self-stretch justify-start items-start flex-col gap-6">
              <div className="flex self-stretch justify-start items-start flex-col gap-5 px-[32px]">
                <div className="flex self-stretch justify-start items-start flex-col gap-5">
                  <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-5 gap-x-4">
                    <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col gap-1">
                      <p className="self-stretch text-[#181D27] text-2xl  font-semibold">
                        Calendar
                      </p>
                      <p className="self-stretch text-[#535862] ">
                        Here is a complete overview of your jobs.
                      </p>
                    </div>
                    <div className="flex justify-start items-center flex-row gap-3">
                   
                      <div
                        onClick={handleOpen}
                        className="flex justify-center items-center flex-row gap-1 py-2.5 px-3.5 bg-[#19BFB7] border-solid border-[undefined] border-2 rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)]"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.99996 4.16602V15.8327M4.16663 9.99935H15.8333"
                            stroke="white"
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex justify-center items-center flex-row px-0.5">
                          <span className="text-[#FFFFFF] text-sm  font-semibold">
                            Add
                          </span>
                        </div>
                      </div>
                   <Modal
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                      >
                        <Box sx={style}>
                          <Typography
                            id="modal-modal-title"
                            className="text-gray-700 text-lg font-semibold"
                            component="h3"
                            sx={{
                              color: "gray.700",
                              fontSize: "1.125rem",
                              fontWeight: "600",
                            }}
                          >
                            Schedule Interview
                          </Typography>
                          <Typography
                            id="modal-modal-description"
                            sx={{
                              color: "gray",
                              fontSize: ".75rem",
                              paddingBottom: "1rem",
                            }}
                          >
                            Schedule an interview with automatic Google Meet creation
                          </Typography>

                          {googleMeetDetails && (
                            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
                              <p className="font-semibold mb-2">Google Meet Created Successfully!</p>
                              <div className="space-y-2">
                                <div>
                                  <span className="font-medium">Meeting Link:</span>
                                  <a 
                                    href={googleMeetDetails.meet_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-blue-700 underline block break-all text-sm"
                                  >
                                    {googleMeetDetails.meet_link}
                                  </a>
                                </div>
                                {googleMeetDetails.meeting_code && (
                                  <div>
                                    <span className="font-medium">Meeting Code:</span> {googleMeetDetails.meeting_code}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={handleClose}
                                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 w-full"
                              >
                                Close
                              </button>
                            </div>
                          )}

                          <Typography variant="div" component="div">
                            <div className="flex w-full flex-col items-center gap-5 border-t border-gray-300 pt-4 justify-center">
                              {/* Job Selection */}
                              <div className="w-full flex flex-col gap-2">
                                <h5 className="text-gray-700 text-sm font-semibold">
                                  Job Position <span className="text-red-500">*</span>
                                </h5>
                                <div className="border border-gray-300 rounded-lg p-2">
                                  <select
                                    className="text-gray-700 w-full outline-none"
                                    name="job"
                                    value={newMeeting.job}
                                    onChange={handleChange}
                                    required
                                  >
                                    <option value="">Select a job</option>
                                    {jobs.map((job) => (
                                      <option key={job.id} value={job.id}>
                                        {job.title} - {job.company}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* Candidate Selection */}
                              <div className="w-full flex flex-col gap-2">
                                <h5 className="text-gray-700 font-semibold">
                                  Candidate (Applicant) <span className="text-red-500">*</span>
                                </h5>
                                <div className="border border-gray-300 rounded-lg p-2">
                                  <select
                                    className="text-gray-700 w-full outline-none"
                                    name="interviewee"
                                    value={newMeeting.interviewee}
                                    onChange={handleChange}
                                    disabled={!newMeeting.job || loading}
                                    required
                                  >
                                    <option value="">
                                      {!newMeeting.job 
                                        ? "Select a job first" 
                                        : applicants.length === 0 
                                          ? "No applicants found" 
                                          : "Choose candidate"}
                                    </option>
                                    {applicants.map((applicant) => (
                                      <option key={applicant.id} value={applicant.id}>
                                        Applicant #{applicant.id}
                                        {applicant.user_email && ` - ${applicant.user_email}`}
                                        {applicant.job_title && ` (${applicant.job_title})`}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {loading && newMeeting.job && (
                                  <p className="text-sm text-gray-500">Loading applicants...</p>
                                )}
                              </div>

                              {/* Date & Time */}
                              <div className="w-full text-gray-700 flex flex-col gap-3 justify-between items-start">
                                <h5 className="text-gray-700 text-sm font-semibold">
                                  Date & Time <span className="text-red-500">*</span>
                                </h5>
                                <div className="flex gap-2 items-center w-full">
                                  <input
                                    type="date"
                                    name="interview_date"
                                    value={newMeeting.interview_date}
                                    onChange={handleChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="border border-gray-300 p-2 w-full rounded-lg outline-none"
                                    required
                                  />
                                  <input
                                    type="time"
                                    name="interview_time"
                                    value={newMeeting.interview_time}
                                    onChange={handleChange}
                                    placeholder="Time"
                                    className="border border-gray-300 p-2 w-full rounded-lg outline-none"
                                    required
                                  />
                                </div>
                              </div>

                              {/* Duration */}
                              <div className="w-full flex flex-col gap-2">
                                <h5 className="text-gray-700 font-semibold">
                                  Duration (minutes) <span className="text-red-500">*</span>
                                </h5>
                                <input
                                  type="number"
                                  name="duration_minutes"
                                  value={newMeeting.duration_minutes}
                                  onChange={handleChange}
                                  min="15"
                                  step="15"
                                  className="text-gray-700 w-full border-gray-300 border rounded-lg outline-none p-2"
                                />
                              </div>

                              {/* Timezone */}
                              <div className="w-full flex flex-col gap-2">
                                <h5 className="text-gray-700 font-semibold">
                                  Timezone <span className="text-red-500">*</span>
                                </h5>
                                <input
                                  type="text"
                                  name="timezone"
                                  value={newMeeting.timezone}
                                  onChange={handleChange}
                                  className="text-gray-700 w-full border-gray-300 border rounded-lg outline-none p-2"
                                  placeholder="e.g., America/New_York"
                                />
                                <p className="text-xs text-gray-500">
                                  Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                </p>
                              </div>

                              {/* Notes */}
                              <div className="w-full flex flex-col gap-2">
                                <h5 className="text-gray-700 font-semibold">
                                  Notes (Optional)
                                </h5>
                                <textarea
                                  name="notes"
                                  value={newMeeting.notes}
                                  onChange={handleChange}
                                  placeholder="Enter any additional notes"
                                  className="text-gray-700 resize-none h-20 w-full border-gray-300 border rounded-lg outline-none p-2"
                                />
                              </div>

                              {/* Google Meet Info */}
                              <div className="text-gray-700 flex items-center gap-4 py-2 w-full bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <div>
                                  <img src={GoogleMeetIcon} alt="Google Meet" className="w-8 h-8" />
                                </div>
                                <p className="text-sm text-gray-600">
                                  Google Meet link will be automatically generated
                                </p>
                              </div>
                            </div>
                          </Typography>

                          <Typography component="div">
                            <div className="flex gap-5 items-center w-full mt-4">
                              <button
                                className="rounded-lg px-4 py-2 border border-gray-300 text-gray-700 w-full cursor-pointer hover:bg-gray-50"
                                onClick={handleClose}
                              >
                                Cancel
                              </button>
                              <button
                                className="text-white bg-[#009C97] rounded-lg px-4 py-2 w-full cursor-pointer hover:bg-[#008580] disabled:opacity-50"
                                onClick={handleSubmit}
                                disabled={loading}
                              >
                                {loading ? 'Scheduling...' : 'Schedule Interview'}
                              </button>
                            </div>
                          </Typography>
                        </Box>
                      </Modal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex self-stretch justify-start items-start flex-row gap-6">
              <div className="flex flex-1 justify-start items-start flex-row gap-4 px-[32px]">
                <div className="flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <div className="flex self-stretch justify-start items-start flex-col gap-5 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b">
                    <div className="flex self-stretch justify-start items-start flex-row gap-4 py-[20px] px-[24px]">
                      <div className="flex flex-1 justify-start items-start flex-row gap-3">
                        <div
                          className="flex justify-start items-center flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-lg w-[64px]"
                          style={{ width: "64px" }}
                        >
                          <div className="flex self-stretch justify-center items-center flex-row pt-1 pb-0.5 px-2 bg-[#FAFAFA]">
                            <span className="text-[#717680] text-xs  font-semibold">
                              JAN
                            </span>
                          </div>
                          <div className="flex self-stretch justify-center items-center flex-col pt-[1px] pb-[3px] px-2">
                            <span className="text-[#009C97] text-lg  font-bold">
                              10
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-start flex-col gap-0.5">
                          <div className="flex justify-start items-center flex-row gap-2">
                            <span className="text-[#181D27] text-lg  font-semibold">
                              January 2025
                            </span>
                            <div className="flex justify-start items-center flex-row py-0.5 px-1.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-md shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                              <span className="text-[#414651] text-xs  text-center font-medium">
                                Week 1
                              </span>
                            </div>
                          </div>
                          <p className="self-stretch text-[#535862] text-sm ">
                            Jan 1, 2025 – Jan 31, 2025
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-start items-center flex-row gap-3">
                        <div className="flex justify-center items-center flex-row p-2.5 rounded-lg">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                              stroke="#535862"
                              strokeWidth="1.66667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="flex justify-start items-start flex-row border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)]">
                          <div className="min-h-[40px] flex justify-center items-center flex-row gap-2 py-2 px-3 bg-[#FFFFFF] border-solid border-[#D5D7DA] border-r">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15.8333 9.99935H4.16663M4.16663 9.99935L9.99996 15.8327M4.16663 9.99935L9.99996 4.16602"
                                stroke="#414651"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="min-h-[40px] flex justify-center items-center flex-row gap-2 py-2 px-3 bg-[#FFFFFF] border-solid border-[#D5D7DA] border-r">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.99996 4.16602V15.8327M4.16663 9.99935H15.8333"
                                stroke="#414651"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="min-h-[40px] flex justify-center items-center flex-row gap-2 py-2 px-3 bg-[#FFFFFF] border-solid border-[#D5D7DA] border-r">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.16663 9.99935H15.8333M15.8333 9.99935L9.99996 4.16602M15.8333 9.99935L9.99996 15.8327"
                                stroke="#414651"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex justify-start items-start flex-col">
                          <div className="flex justify-start items-start flex-col">
                            <div className="flex justify-between items-center text-gray-700 text-sm py-1 px-2 bg-white border border-gray-300  rounded-lg ">
                              {/* <div className="flex justify-center items-center flex-row px-0.5">
                                <span className="text-[#414651] text-sm  font-semibold">
                                  Week view
                                </span>
                              </div>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 7.5L10 12.5L15 7.5"
                                  stroke="#414651"
                                  strokeWidth="1.66667"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg> */}
                              <select
                                className="w-full outline-none"
                                onChange={(e) => setPage(e.target.value)}
                              >
                                <option value="weekView">Week view</option>
                                <option value="dayview">Day view</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {page === "weekView" ? (
                    <div className="flex self-stretch flex-1 justify-start items-start flex-col relative">
                      <div
                        className="flex justify-start items-center flex-row shadow-sm absolute w-[1116px]"
                        style={{
                          top: "0px",
                          height: "40px",
                          left: "0px",
                          right: "0px",
                          width: "calc(100% - 0px - 0px)",
                        }}
                      >
                        <div
                          className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r w-[72px]"
                          style={{ width: "72px" }}
                        ></div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Mon
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              6
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Tue
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              7
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Wed
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              8
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Thur
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              9
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-2 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Fri
                          </span>
                          <div
                            className="flex justify-center items-center flex-col bg-[#009C97] rounded-full w-[24px] h-[24px]"
                            style={{ width: "24px" }}
                          >
                            <p className="self-stretch text-[#FFFFFF] text-xs  text-center font-semibold">
                              10
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Sat
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              11
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-center items-center flex-row gap-1 p-2 bg-[#FFFFFF] border-solid border-[#E9EAEB] border-b border-r">
                          <span className="text-[#717680] text-xs  font-medium">
                            Sun
                          </span>
                          <div className="flex justify-center items-center flex-col rounded-full h-[24px]">
                            <p className="self-stretch text-[#414651] text-xs  font-semibold">
                              12
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex self-stretch flex-1 justify-start items-start flex-row pt-2 relative">
                        <div className="flex justify-start items-start flex-col">
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              8 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              9 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "36px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              10 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "34px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              11 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "35px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              12 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "28px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              1 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "29px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              2 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              3 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              4 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs  text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              5 PM
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            {interviews.map((interview) => {
                              const { top, height } = calculateEventPosition(interview.interview_date);
                              const eventColor = getEventColor(interview.is_upcoming ? "upcoming" : interview.is_active ? "completed" : "cancelled");
                              const eventBorderColor = getEventBorderColor(interview.is_upcoming ? "upcoming" : interview.is_active ? "completed" : "cancelled");

                              return (
                                <div
                                  key={interview.id}
                                  className="flex justify-start items-start flex-col p-1.5 absolute w-[149px]"
                                  style={{
                                    top: `${top}px`,
                                    height: `${height}px`,
                                    left: "0px",
                                    right: "0.14px",
                                    width: "calc(100% - 0px - 0.14px)",
                                  }}
                                >
                                  <div
                                    className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 rounded-md"
                                    style={{
                                      backgroundColor: eventColor,
                                      border: `1px solid ${eventBorderColor}`,
                                    }}
                                  >
                                    <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                      <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                        <p className="flex-1 text-[#414651] text-xs  font-semibold">
                                          Interview: {interview.job.title}
                                        </p>
                                      </div>
                                      <p className="self-stretch text-[#535862] text-xs ">
                                        With: {interview.interviewee.username}
                                      </p>
                                      <p className="self-stretch text-[#535862] text-xs ">
                                        Time: {moment(interview.interview_date).format("h:mm A")}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Fill remaining time slots with empty divs */}
                            {[...Array(24)].map((_, index) => {
                              const hour = index + 8; // Assuming calendar starts at 8 AM
                              const hasEvent = interviews.some(interview => {
                                const interviewHour = moment(interview.interview_date).hours();
                                return interviewHour === hour;
                              });
                              if (!hasEvent) {
                                return (
                                  <div
                                    key={`empty-${index}`}
                                    className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"
                                  ></div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FDF2FA] border-solid border-[#FCCEEE] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#C11574] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#DD2590] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[144px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#F5FFF8] border-solid border-[#D7FEE7] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#009C97] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#009C97] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[144px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#EFF8FF] border-solid border-[#B2DDFF] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#175CD3] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#1570EF] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#EEF4FF] border-solid border-[#C7D7FE] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#3538CD] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#444CE7] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#535862] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-center items-start flex-col p-1.5 absolute w-[149px] h-[192px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FEF6EE] border-solid border-[#F9DBAF] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#B93815] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#E04F16] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#ffffff]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#ffffff]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#ffffff]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#EDFCF2] border-solid border-[#AAF0C4] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#087443] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#099250] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[48px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs  font-semibold">
                                      Friday standup
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#F4F3FF] border-solid border-[#D9D6FE] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#5925DC] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#6938EF] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#ffffff]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-center items-start flex-col p-1.5 absolute w-[149px] h-[192px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#EEF4FF] border-solid border-[#C7D7FE] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#3538CD] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#444CE7] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[48px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FEFBE8] border-solid border-[#FEEE95] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#A15C07] text-xs  font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#CA8504] text-xs ">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b border-r h-[48px]"></div>
                          </div>
                        </div>
                        <div
                          className="absolute"
                          style={{
                            right: "0px",
                            width: "16px",
                            top: "40px",
                            bottom: "0px",
                            height: "calc(100% - 40px - 0px)",
                          }}
                        >
                          <svg
                            width="16"
                            height="776"
                            viewBox="0 0 16 776"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              opacity="0.15"
                              d="M8 3.5C5.51472 3.5 3.5 5.51473 3.5 8.00001V412C3.5 414.485 5.51472 416.5 8 416.5C10.4853 416.5 12.5 414.485 12.5 412V8C12.5 5.51472 10.4853 3.5 8 3.5Z"
                              fill="black"
                              stroke="white"
                            />
                          </svg>
                        </div>
                        <div
                          className="flex justify-start items-center flex-row gap-1 absolute w-[1116px]"
                          style={{
                            top: "604px",
                            height: "18px",
                            left: "0px",
                            right: "0px",
                            width: "calc(100% - 0px - 0px)",
                          }}
                        >
                          <p className="text-[#009C97] text-xs  text-right font-medium">
                            2:20 PM
                          </p>
                          <svg
                            width="1048"
                            height="8"
                            viewBox="0 0 1048 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="4" cy="4" r="4" fill="#009C97" />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1048 4C1048 4.27614 1047.78 4.5 1047.5 4.5H6.5C6.22386 4.5 6 4.27614 6 4C6 3.72386 6.22383 3.5 6.49997 3.5H1047.5C1047.78 3.5 1048 3.72386 1048 4Z"
                              fill="#009C97"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex self-stretch flex-1 justify-start items-start flex-col relative">
                      <div className="flex self-stretch flex-1 justify-start items-start flex-row pt-2 relative">
                        <div className="flex justify-start items-start flex-col">
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB]  w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              8 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r  w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              9 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "36px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              10 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "34px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              11 AM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "35px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              12 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "28px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              1 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "29px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              2 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              3 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              4 PM
                            </span>
                          </div>
                          <div
                            className="flex justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border-r w-[72px] h-[96px] relative"
                            style={{ width: "72px" }}
                          >
                            <span
                              className="text-[#717680] text-xs font-['Inter'] text-right font-medium absolute"
                              style={{
                                right: "8px",
                                width: "30px",
                                top: "-8px",
                                height: "18px",
                              }}
                            >
                              5 PM
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[48px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs font-['Inter'] font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[96px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs font-['Inter'] font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                  <p className="self-stretch text-[#535862] text-xs font-['Inter']">
                                    9:00 AM
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div
                              className="flex justify-start items-start flex-col p-1.5 absolute w-[149px] h-[48px]"
                              style={{
                                top: "0px",
                                bottom: "0px",
                                height: "calc(100% - 0px - 0px)",
                                left: "0px",
                                right: "0.14px",
                                width: "calc(100% - 0px - 0.14px)",
                              }}
                            >
                              <div className="flex self-stretch flex-1 justify-start items-start flex-row gap-1 py-1.5 px-2 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-md">
                                <div className="flex flex-1 justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-start flex-row gap-0.5">
                                    <p className="flex-1 text-[#414651] text-xs font-['Inter'] font-semibold">
                                      Monday standup
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div className="flex flex-1 justify-start items-start flex-col">
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>

                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] relative">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                          <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF]">
                            <div className="flex self-stretch justify-start items-start flex-col p-2 border-solid border-[#E9EAEB] border-b  h-[48px]"></div>
                          </div>
                        </div>
                        <div
                          className="absolute"
                          style={{
                            right: "0px",
                            width: "16px",
                            top: "40px",
                            bottom: "0px",
                            height: "calc(100% - 40px - 0px)",
                          }}
                        >
                          <svg
                            width="16"
                            height="776"
                            viewBox="0 0 16 776"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              opacity="0.15"
                              d="M8 3.5C5.51472 3.5 3.5 5.51473 3.5 8.00001V412C3.5 414.485 5.51472 416.5 8 416.5C10.4853 416.5 12.5 414.485 12.5 412V8C12.5 5.51472 10.4853 3.5 8 3.5Z"
                              fill="black"
                              stroke="white"
                            />
                          </svg>
                        </div>
                        <div
                          className="flex justify-start items-center flex-row gap-1 absolute w-[1116px]"
                          style={{
                            top: "604px",
                            height: "18px",
                            left: "0px",
                            right: "0px",
                            width: "calc(100% - 0px - 0px)",
                          }}
                        >
                          <p className="text-[#009C97] text-xs font-['Inter'] text-right font-medium">
                            2:20 PM
                          </p>
                          <svg
                            width="1048"
                            height="8"
                            viewBox="0 0 1048 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="4" cy="4" r="4" fill="#009C97" />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1048 4C1048 4.27614 1047.78 4.5 1047.5 4.5H6.5C6.22386 4.5 6 4.27614 6 4C6 3.72386 6.22383 3.5 6.49997 3.5H1047.5C1047.78 3.5 1048 3.72386 1048 4Z"
                              fill="#009C97"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      ;
    </div>
  );
};

export default Calendar;
